/**
 * Iter-228x-quater — Dashboard banner that surfaces TEST_-prefixed missions
 * leaked into the production scope. CEO/ADMIN only.
 *
 * Behavior:
 *  - Polls `GET /api/admin/test-data-leaks` once on mount.
 *  - If `leaked_missions === 0`, renders nothing.
 *  - Otherwise shows an amber banner with counts + "Voir le détail" + "Lancer
 *    le nettoyage".
 *  - Cleanup is double-confirmed (modal with typed-confirm "NETTOYER").
 *  - CEO-only for cleanup; ADMIN sees the banner + detail but the button is
 *    disabled with a tooltip.
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function TestDataLeakBanner({ user }) {
  const [report, setReport] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);

  const isCEO = user?.role === "CEO";
  const isPrivileged = isCEO || user?.role === "ADMIN";

  const fetchReport = () => {
    api.get("/admin/test-data-leaks")
      .then((r) => setReport(r.data))
      .catch(() => setReport(null));
  };

  useEffect(() => {
    if (!isPrivileged) return;
    fetchReport();
  }, [isPrivileged]);

  if (!isPrivileged || !report || (report.leaked_missions || 0) === 0) return null;

  const runCleanup = async () => {
    if (confirmText !== "NETTOYER") {
      toast.error("Saisissez exactement « NETTOYER » pour confirmer.");
      return;
    }
    setRunning(true);
    try {
      const r = await api.post("/admin/test-data-leaks/cleanup");
      const d = r.data?.deleted || {};
      toast.success(
        `Nettoyage terminé : ${d.missions || 0} missions, ${d.invoices || 0} factures, ${d.purchase_orders || 0} BCs supprimés.`,
      );
      setShowConfirm(false);
      setConfirmText("");
      fetchReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du nettoyage");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div
        className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-start gap-3 shadow-sm"
        data-testid="test-data-leak-banner"
      >
        <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-[260px]">
          <div className="text-sm font-semibold text-amber-900">
            {report.leaked_missions} mission{report.leaked_missions > 1 ? "s" : ""} TEST_ détectée{report.leaked_missions > 1 ? "s" : ""} en scope production
          </div>
          <div className="text-xs text-amber-800 mt-1 font-mono">
            <span className="mr-3">Factures actives liées : <strong>{report.leaked_active_invoices}</strong></span>
            <span className="mr-3">Factures totales : <strong>{report.leaked_invoices}</strong></span>
            <span className="mr-3">BCs : <strong>{report.leaked_pos}</strong></span>
            <span>Lignes CRA : <strong>{report.leaked_cra_entries}</strong></span>
          </div>
          <div className="text-[11px] text-amber-700 mt-1">
            Ces entités ont été créées par d&apos;anciennes exécutions de tests automatisés et polluent les rapports de production. Le nettoyage est irréversible.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            data-testid="test-leak-details-btn"
            className="px-3 py-1.5 text-xs rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
          >
            Voir le détail
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!isCEO}
            title={isCEO ? "" : "Action réservée au CEO"}
            data-testid="test-leak-cleanup-btn"
            className={`px-3 py-1.5 text-xs rounded border ${isCEO ? "border-red-600 bg-red-600 text-white hover:bg-red-700" : "border-zinc-300 bg-zinc-100 text-zinc-400 cursor-not-allowed"}`}
          >
            Lancer le nettoyage
          </button>
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-3xl" data-testid="test-leak-details-modal">
          <DialogHeader>
            <DialogTitle>Missions TEST_ détectées en production</DialogTitle>
            <DialogDescription>
              Liste partielle ({(report.sample_titles || []).length} sur {report.total_titles || report.leaked_missions}).
              Toutes seront supprimées avec leurs factures/CRAs/BCs lors du nettoyage.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto border border-zinc-200 rounded">
            <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full text-xs" data-testid="test-leak-details-table">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="px-3 py-2 text-left">Mission ID</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Devise</th>
                  <th className="px-3 py-2 text-left">Titre</th>
                </tr>
              </thead>
              <tbody>
                {(report.sample_titles || []).map((m) => (
                  <tr key={m.mission_id} className="border-t border-zinc-100">
                    <td className="px-3 py-2 font-mono">{m.mission_id}</td>
                    <td className="px-3 py-2">{m.type || "—"}</td>
                    <td className="px-3 py-2 font-mono text-zinc-600">{m.currency || "—"}</td>
                    <td className="px-3 py-2 truncate max-w-[400px]" title={m.title}>{m.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm cleanup modal */}
      <Dialog open={showConfirm} onOpenChange={(o) => { if (!running) setShowConfirm(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="max-w-md" data-testid="test-leak-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={18} /> Confirmer le nettoyage
            </DialogTitle>
            <DialogDescription>
              Action <strong>irréversible</strong>. {report.leaked_missions} mission{report.leaked_missions > 1 ? "s" : ""} TEST_,{" "}
              {report.leaked_invoices} facture{report.leaked_invoices > 1 ? "s" : ""}, {report.leaked_pos} BC{report.leaked_pos > 1 ? "s" : ""} et leurs entités liées seront supprimés définitivement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-mono uppercase tracking-wider text-zinc-600 block">
              Tapez <strong className="text-red-700">NETTOYER</strong> pour confirmer
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="test-leak-confirm-input"
              autoFocus
              disabled={running}
              className="w-full px-3 py-2 border border-zinc-300 rounded text-sm font-mono focus:border-red-500 focus:outline-none"
              placeholder="NETTOYER"
            />
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                disabled={running}
                className="px-3 py-1.5 text-xs rounded border border-zinc-300 bg-white hover:bg-zinc-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={runCleanup}
                disabled={running || confirmText !== "NETTOYER"}
                data-testid="test-leak-confirm-run-btn"
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {running && <Loader2 size={12} className="animate-spin" />}
                {running ? "Nettoyage en cours…" : "Lancer le nettoyage"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
